import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect, useCallback, useRef } from "react";
import {
  fetchSubsAppointments,
  fetchSubsAppointmentFilterOptions,
  setSubsPage,
  setSubsPageSize,
  setSubsSearch,
  setSubsServiceParent,
  setSubsSortKey,
  setSubsStatus,
  setSubsDateFrom,
  setSubsDateTo,
  updateAppointmentInList,
} from "./appointments.slice";
import { useDebounce } from "@/hooks/useDebounce";

export const useSubsAppointments = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.subsAppointments);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce search input by 400ms to prevent excessive API calls
  // Keep Redux search updating immediately for responsive input display
  // Pass debounced value directly to fetch - no sync needed, prevents blocking input
  const debouncedSearch = useDebounce(state.filters.search, 400);

  useEffect(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const fetchData = async () => {
      try {
        // Pass debounced search directly to fetch - this prevents requests on every keystroke
        // while keeping the input responsive (no sync back to Redux while typing)
        await dispatch(fetchSubsAppointments(debouncedSearch)).unwrap();
      } catch (error: any) {
        // Ignore cancelled requests
        if (error.message !== "Request cancelled") {
          console.error("Failed to fetch subs appointments:", error);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    dispatch,
    state.filters.status,
    state.filters.serviceParent,
    debouncedSearch, // Use debounced search - fetch only triggers after user stops typing
    state.filters.dateFrom,
    state.filters.dateTo,
    state.filters.sortKey,
    state.filters.sortOrder,
    state.pagination.page,
    state.pagination.pageSize,
  ]);

  useEffect(() => {
    dispatch(fetchSubsAppointmentFilterOptions());
  }, [dispatch]);

  // Memoize filter setters to prevent unnecessary re-renders
  const setStatusCallback = useCallback((v: string) => dispatch(setSubsStatus(v)), [dispatch]);
  const setServiceParentCallback = useCallback((v: string) => dispatch(setSubsServiceParent(v)), [dispatch]);
  const setSearchCallback = useCallback((v: string) => dispatch(setSubsSearch(v)), [dispatch]);
  const setDateFromCallback = useCallback((v: string) => dispatch(setSubsDateFrom(v)), [dispatch]);
  const setDateToCallback = useCallback((v: string) => dispatch(setSubsDateTo(v)), [dispatch]);
  const setSortKeyCallback = useCallback((k: string) => dispatch(setSubsSortKey(k)), [dispatch]);
  const setPageCallback = useCallback((p: number) => dispatch(setSubsPage(p)), [dispatch]);
  const setPageSizeCallback = useCallback((ps: number) => dispatch(setSubsPageSize(ps)), [dispatch]);

  const refreshAppointments = useCallback(() => {
    dispatch(fetchSubsAppointments(undefined));
  }, [dispatch]);

  return {
    list: state.list,
    total: state.total,
    loading: state.loading,
    options: state.options,

    filters: {
      ...state.filters,
      setStatus: setStatusCallback,
      setServiceParent: setServiceParentCallback,
      setSearch: setSearchCallback,
      setDateFrom: setDateFromCallback,
      setDateTo: setDateToCallback,
      setSortKey: setSortKeyCallback,
    },

    pagination: {
      ...state.pagination,
      setPage: setPageCallback,
      setPageSize: setPageSizeCallback,
    },

    // actions
    updateAppointmentInList: (appointment: any) => dispatch(updateAppointmentInList(appointment)),
    refreshAppointments: async () => {
      try {
        await dispatch(fetchSubsAppointments(state.filters.search)).unwrap();
      } catch (error: any) {
        if (error.message !== "Request cancelled") {
          console.error("Failed to refresh appointments:", error);
        }
      }
    },

    dispatch,
  };
};
